"use client";
import { useEffect, useState } from "react";

import useLocaleLegacy from "@/hooks/use-locale-legacy";
import { calculateKPA } from "@/lib/calculate-kpa";
import { toIDR } from "@/lib/to-idr";

import kpaLocale from "@/locales/kpa-simulation.json";

export default function KPASimulation() {
  const { t } = useLocaleLegacy(kpaLocale);
  const [loan_amount, setLoan_amount] = useState("");
  const [loan_term, setLoan_term] = useState("");
  const [propertyPrice, setPropertyPrice] = useState("");

  const [dp, setDp] = useState("");

  const [interest_rate, setInterest_rate] = useState("");

  const [result, setResult] = useState(0);

  const onCalculate = () => {
    const _propertyPrice = parseInt(propertyPrice.replace(/\D/g, ""), 10);
    const downPayment = parseInt(dp.replace(/\D/g, ""), 10);
    const loanTermYears = parseInt(loan_term, 10);
    const annualInterestRate = parseFloat(
      interest_rate.replace(/[^0-9.]/g, "")
    );

    const kpa = calculateKPA(
      _propertyPrice,
      downPayment,
      annualInterestRate,
      loanTermYears
    );

    !Number.isNaN(kpa) && setResult(kpa);
  };

  useEffect(() => {
    if (propertyPrice && dp) {
      const _propertyPrice = parseInt(propertyPrice.replace(/\D/g, ""), 10);
      let downPayment = parseInt(dp.replace(/\D/g, ""), 10);
      if (downPayment > _propertyPrice) downPayment = 0;
      setLoan_amount(toIDR(String(_propertyPrice - downPayment)));
    }
  }, [propertyPrice, dp]);

  return (
    <div className="py-9 xl:py-24 bg-[#FFF9F1]">
      <div className="w-full xl:max-w-screen-xl mx-auto px-10 xl:px-0">
        <div className="w-3/4 xl:w-1/2">
          <h1 className="text-darkgold text-[11px] xl:text-2xl mb-1.5 xl:mb-3 font-medium">
            {t("title")}
          </h1>
          <h2
            className="text-darkgold xl:text-4xl break-words font-pluto"
            dangerouslySetInnerHTML={{ __html: t("description") }}
          ></h2>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 mt-5 xl:mt-9 gap-y-6 xl:gap-y-8 gap-x-24">
          <div className="flex flex-col">
            <label
              htmlFor=""
              className="font-medium text-brown text-[11px] xl:text-2xl"
            >
              {t("input_1_label")}
            </label>
            <input
              placeholder={t("input_1_placeholder")}
              className="text-[12px] xl:text-2xl placeholder:text-gold text-gold border border-transparent border-b-brown focus:outline-none w-full xl:pb-3 outline-none"
              value={propertyPrice}
              onChange={(e) => {
                const rawValue = e.target.value;
                const formatted = toIDR(rawValue);
                setPropertyPrice(formatted);
              }}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor=""
              className="font-medium text-brown text-[11px] xl:text-2xl"
            >
              {t("input_2_label")}
            </label>

            <div className="flex w-full itks-center border border-transparent border-b-brown">
              <input
                placeholder={t("input_2_placeholder")}
                className="text-[12px] xl:text-2xl placeholder:text-gold text-gold focus:outline-none w-full xl:pb-3"
                value={loan_term}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setLoan_term(rawValue);
                }}
              />
              <span className="font-medium text-brown">{t("years_label")}</span>
            </div>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor=""
              className="font-medium text-brown text-[11px] xl:text-2xl"
            >
              {t("input_3_label")}
            </label>

            <input
              placeholder={t("input_3_placeholder")}
              className="text-[12px] xl:text-2xl placeholder:text-gold text-gold border border-transparent border-b-brown focus:outline-none w-full xl:pb-3"
              value={dp}
              onChange={(e) => {
                const rawValue = e.target.value;
                const formatted = toIDR(rawValue);
                setDp(formatted);
              }}
            />
          </div>

          <div className="flex flex-col">
            <label
              htmlFor=""
              className="font-medium text-brown text-[11px] xl:text-2xl"
            >
              {t("input_4_label")}
            </label>

            <div className="w-full inline-flex items-center">
              <div className="flex items-center w-full border border-transparent border-b-brown">
                <input
                  placeholder={t("input_4_placeholder")}
                  className="text-[12px] xl:text-2xl placeholder:text-gold text-gold focus:outline-none w-full xl:pb-3"
                  value={interest_rate}
                  onChange={(e) => {
                    const rawValue = e.target.value;
                    setInterest_rate(rawValue);
                  }}
                />
                <span className="font-medium text-brown">%</span>
              </div>
              <button
                onClick={onCalculate}
                className="rounded-4xl font-medium text-gold bg-white py-3 px-6 ml-6 cursor-pointer font-pluto text-[12px] xl:text-2xl hidden xl:block"
              >
                {t("calculate_btn_label")}
              </button>
            </div>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor=""
              className="font-medium text-brown text-[11px] xl:text-2xl"
            >
              {t("input_5_label")}
            </label>

            <input
              placeholder={t("input_5_placeholder")}
              className="text-[12px] xl:text-2xl placeholder:text-gold text-gold border border-transparent border-b-brown focus:outline-none w-full xl:pb-3"
              value={loan_amount}
              disabled
              // onChange={(e) => {
              //   const rawValue = e.target.value;
              //   const formatted = toIDR(rawValue);
              //   setDp(formatted);
              // }}
            />

            <p className="text-darkgold text-[7px] xl:text-base mt-2 xl:mt-9 font-medium" dangerouslySetInnerHTML={{__html: 
              `*${t("simulation_note")}`
            }}>
            </p>
          </div>

          <div>
            {Boolean(result) && (
              <div className="rounded-sm w-full bg-linen p-5 mt-5 flex flex-col">
                <p className="font-medium text-gold text-center text-[11px] xl:text-base mt-2 xl:mt-0">
                  Estimated Monthly Installments
                </p>
                <p className="text-red-500 font-medium text-center text-[11px] xl:text-2xl">
                  Rp {Math.round(result).toLocaleString("id-ID")}
                </p>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={onCalculate}
          className="rounded-4xl font-medium text-gold bg-white py-3 px-6 mt-3 cursor-pointer font-pluto text-[12px] xl:text-2xl block xl:hidden mx-auto"
        >
          CALCULATE
        </button>
      </div>
    </div>
  );
}
